import { Check, Cloud, Trash2, LogOut } from 'lucide-react';
import { Logo } from '@/components/logo';
import { useApp } from '@/context/app-context';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { APP_VERSION } from '@/lib/constants';

export function Settings() {
  const navigate = useNavigate();
  const { state, dispatch, manualSync, sheetsStatus } = useApp();
  const [aiProvider, setAiProvider] = useState(state.aiProvider || 'openai');
  const [aiApiKey, setAiApiKey] = useState(state.aiApiKey || '');
  const [weight, setWeight] = useState(String(state.bodyStats.weight || ''));
  const [height, setHeight] = useState(String(state.bodyStats.height || ''));
  const [bodyFat, setBodyFat] = useState(String(state.bodyStats.bodyFat || ''));
  const [bodySaveSuccess, setBodySaveSuccess] = useState(false);
  const [aiSaveSuccess, setAiSaveSuccess] = useState(false);

  const handleUnitToggle = () => {
    const newUnit = state.unitPreference === 'kg' ? 'lbs' : 'kg';
    dispatch({ type: 'SET_UNIT_PREFERENCE', unit: newUnit });
  };

  const handleSaveBodyStats = () => {
    dispatch({
      type: 'SET_BODY_STATS',
      stats: {
        weight: weight ? parseFloat(weight) : undefined,
        height: height ? parseFloat(height) : undefined,
        bodyFat: bodyFat ? parseFloat(bodyFat) : undefined,
      },
    });
    setBodySaveSuccess(true);
    setTimeout(() => setBodySaveSuccess(false), 2000);
  };

  const handleSaveAI = () => {
    dispatch({ type: 'SET_AI_PROVIDER', provider: aiProvider });
    dispatch({ type: 'SET_AI_API_KEY', key: aiApiKey });
    setAiSaveSuccess(true);
    setTimeout(() => setAiSaveSuccess(false), 2000);
  };

  const handleSignOut = () => {
    dispatch({ type: 'SET_GAPI_TOKEN', token: null });
    navigate('/login');
  };

  const handleResetData = () => {
    if (window.confirm('Are you sure you want to reset all data? This cannot be undone.')) {
      dispatch({ type: 'SET_GAPI_TOKEN', token: null });
      localStorage.clear();
      window.location.href = '/login';
    }
  };

  const workoutCount = Object.keys(state.workouts).length;
  const sessionCount = state.history.length;
  const weightEntries = state.weightLog.length;

  return (
    <div className="p-5 pb-24" style={{ backgroundColor: '#18181B' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-5" style={{ height: '32px' }}>
        <Logo />
        <div
          className="px-2.5 py-1 rounded-full flex items-center gap-1"
          style={{ backgroundColor: '#1F4428', color: '#4ADE80', fontSize: '11px', fontWeight: 600 }}
        >
          <Check size={10} />
          <span>{!state.gapiToken ? 'Offline' : sheetsStatus === 'syncing' ? 'Syncing' : sheetsStatus === 'error' ? 'Error' : sheetsStatus === 'paused' ? 'Paused' : 'Synced'}</span>
        </div>
      </div>

      {/* Title */}
      <h2 style={{ color: '#F0F0F0', fontSize: '18px', fontWeight: 600, marginBottom: '20px' }}>
        Settings
      </h2>

      {/* Cloud Sync Section */}
      <div className="mb-6">
        <span style={{ color: '#71717A', fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '12px', letterSpacing: '0.5px' }}>
          CLOUD SYNC
        </span>
        <div
          className="p-4 mb-3"
          style={{
            backgroundColor: '#27272A',
            borderRadius: '16px',
            border: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <Cloud size={20} style={{ color: state.gapiToken ? '#4ADE80' : '#71717A' }} />
              <div>
                <div style={{ color: '#F0F0F0', fontSize: '14px', fontWeight: 600 }}>Cloud Backup</div>
                <div style={{ color: '#71717A', fontSize: '12px' }}>
                  {state.gapiToken ? 'Connected' : 'Not connected'}
                </div>
              </div>
            </div>
            <div
              className="px-3 py-1 rounded-full"
              style={{
                backgroundColor: state.gapiToken ? '#1F4428' : '#3F3F46',
                color: state.gapiToken ? '#4ADE80' : '#71717A',
                fontSize: '11px',
                fontWeight: 600,
              }}
            >
              {state.gapiToken ? 'Active' : 'Inactive'}
            </div>
          </div>
          <button
            onClick={state.gapiToken ? manualSync : undefined}
            disabled={sheetsStatus === 'syncing'}
            className="flex-1 rounded-lg"
            style={{
              backgroundColor: '#FF6B35',
              color: '#18181B',
              padding: '12px',
              fontSize: '13px',
              fontWeight: 600,
              width: '100%',
              opacity: sheetsStatus === 'syncing' ? 0.7 : 1,
            }}
          >
            {sheetsStatus === 'syncing' ? 'Syncing\u2026' : state.gapiToken ? 'Sync Now' : 'Connect'}
          </button>
        </div>
      </div>

      {/* Preferences - Weight Unit */}
      <div className="mb-6">
        <span style={{ color: '#71717A', fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '12px', letterSpacing: '0.5px' }}>
          PREFERENCES
        </span>
        <div
          className="p-4"
          style={{
            backgroundColor: '#27272A',
            borderRadius: '16px',
            border: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          <div className="flex items-center justify-between">
            <div>
              <div style={{ color: '#F0F0F0', fontSize: '14px', fontWeight: 600 }}>Weight Unit</div>
              <div style={{ color: '#71717A', fontSize: '12px' }}>Currently: {state.unitPreference}</div>
            </div>
            <button
              onClick={handleUnitToggle}
              className="rounded-lg px-3 py-1"
              style={{ backgroundColor: '#FF6B35', color: '#18181B', fontSize: '12px', fontWeight: 600 }}
            >
              Toggle
            </button>
          </div>
        </div>
      </div>

      {/* Body Stats */}
      <div className="mb-6">
        <span style={{ color: '#71717A', fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '12px', letterSpacing: '0.5px' }}>
          BODY STATS
        </span>

        <div className="space-y-3">
          <div
            className="p-4"
            style={{
              backgroundColor: '#27272A',
              borderRadius: '16px',
              border: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <div className="mb-4 space-y-3">
              <div>
                <label style={{ color: '#71717A', fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '6px' }}>
                  Weight ({state.unitPreference})
                </label>
                <input
                  type="number"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  className="w-full rounded-lg"
                  style={{
                    backgroundColor: '#3F3F46',
                    color: '#F0F0F0',
                    fontSize: '14px',
                    padding: '10px 12px',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                  placeholder="e.g., 75"
                />
              </div>
              <div>
                <label style={{ color: '#71717A', fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '6px' }}>
                  Height (cm)
                </label>
                <input
                  type="number"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  className="w-full rounded-lg"
                  style={{
                    backgroundColor: '#3F3F46',
                    color: '#F0F0F0',
                    fontSize: '14px',
                    padding: '10px 12px',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                  placeholder="e.g., 180"
                />
              </div>
              <div>
                <label style={{ color: '#71717A', fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '6px' }}>
                  Body Fat (%)
                </label>
                <input
                  type="number"
                  value={bodyFat}
                  onChange={(e) => setBodyFat(e.target.value)}
                  className="w-full rounded-lg"
                  style={{
                    backgroundColor: '#3F3F46',
                    color: '#F0F0F0',
                    fontSize: '14px',
                    padding: '10px 12px',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                  placeholder="e.g., 15"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleSaveBodyStats}
                className="flex-1 rounded-lg"
                style={{
                  backgroundColor: '#FF6B35',
                  color: '#18181B',
                  padding: '12px',
                  fontSize: '13px',
                  fontWeight: 600,
                }}
              >
                Save Stats
              </button>
              {bodySaveSuccess && (
                <div className="flex items-center gap-1" style={{ color: '#4ADE80', fontSize: '12px', fontWeight: 600 }}>
                  <Check size={16} />
                  <span>Saved!</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* AI Coach Configuration */}
      <div className="mb-6">
        <span style={{ color: '#71717A', fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '12px', letterSpacing: '0.5px' }}>
          AI COACH
        </span>

        <div
          className="p-4"
          style={{
            backgroundColor: '#27272A',
            borderRadius: '16px',
            border: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          <div className="space-y-3 mb-4">
            <div>
              <label style={{ color: '#71717A', fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '6px' }}>
                Provider
              </label>
              <select
                value={aiProvider}
                onChange={(e) => setAiProvider(e.target.value)}
                className="w-full rounded-lg"
                style={{
                  backgroundColor: '#3F3F46',
                  color: '#F0F0F0',
                  fontSize: '14px',
                  padding: '10px 12px',
                  border: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                <option value="openai">OpenAI</option>
                <option value="gemini">Google Gemini</option>
              </select>
            </div>
            <div>
              <label style={{ color: '#71717A', fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '6px' }}>
                API Key
              </label>
              <input
                type="password"
                value={aiApiKey}
                onChange={(e) => setAiApiKey(e.target.value)}
                className="w-full rounded-lg"
                style={{
                  backgroundColor: '#3F3F46',
                  color: '#F0F0F0',
                  fontSize: '14px',
                  padding: '10px 12px',
                  border: '1px solid rgba(255,255,255,0.06)',
                }}
                placeholder="Paste your API key"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSaveAI}
              className="flex-1 rounded-lg"
              style={{
                backgroundColor: '#FF6B35',
                color: '#18181B',
                padding: '12px',
                fontSize: '13px',
                fontWeight: 600,
              }}
            >
              Save AI Config
            </button>
            {aiSaveSuccess && (
              <div className="flex items-center gap-1" style={{ color: '#4ADE80', fontSize: '12px', fontWeight: 600 }}>
                <Check size={16} />
                <span>Saved!</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Data Management */}
      <div className="mb-6">
        <span style={{ color: '#71717A', fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '12px', letterSpacing: '0.5px' }}>
          DATA MANAGEMENT
        </span>

        <div className="space-y-3">
          <div
            className="p-4"
            style={{
              backgroundColor: '#27272A',
              borderRadius: '16px',
              border: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <span style={{ color: '#F0F0F0', fontSize: '16px', fontWeight: 700 }}>
                YOUR DATA
              </span>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between py-3 border-b" style={{ borderColor: '#3F3F46' }}>
                <span style={{ color: '#71717A', fontSize: '14px' }}>{workoutCount} workouts</span>
                <Check size={16} style={{ color: '#71717A' }} />
              </div>
              <div className="flex items-center justify-between py-3 border-b" style={{ borderColor: '#3F3F46' }}>
                <span style={{ color: '#71717A', fontSize: '14px' }}>{sessionCount} sessions</span>
                <Check size={16} style={{ color: '#71717A' }} />
              </div>
              <div className="flex items-center justify-between py-3 border-b" style={{ borderColor: '#3F3F46' }}>
                <span style={{ color: '#71717A', fontSize: '14px' }}>{weightEntries} weight entries</span>
                <Check size={16} style={{ color: '#71717A' }} />
              </div>
              <div className="flex items-center justify-between py-3">
                <span style={{ color: '#71717A', fontSize: '14px' }}>Data synced</span>
                {state.gapiToken ? (
                  <Check size={16} style={{ color: '#4ADE80' }} />
                ) : (
                  <span style={{ color: '#71717A', fontSize: '12px' }}>Not synced</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sign Out Button */}
      <div className="mb-6">
        <button
          onClick={handleSignOut}
          className="w-full rounded-lg flex items-center justify-center gap-2"
          style={{
            backgroundColor: 'transparent',
            color: '#A1A1AA',
            padding: '14px',
            fontSize: '14px',
            fontWeight: 600,
            minHeight: '52px',
          }}
        >
          <LogOut size={18} />
          <span>Sign Out</span>
        </button>
      </div>

      {/* Danger Zone */}
      <div className="mb-6">
        <span style={{ color: '#EF4444', fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '12px', letterSpacing: '0.5px' }}>
          DANGER ZONE
        </span>
        <button
          onClick={handleResetData}
          className="w-full rounded-lg flex items-center justify-center gap-2"
          style={{
            backgroundColor: 'transparent',
            color: '#EF4444',
            padding: '14px',
            fontSize: '14px',
            fontWeight: 600,
            minHeight: '52px',
            border: '1px solid #EF4444',
          }}
        >
          <Trash2 size={18} />
          <span>Reset All Data</span>
        </button>
      </div>

      {/* App Info */}
      <div className="mt-8 text-center space-y-2">
        <div style={{ color: '#71717A', fontSize: '12px' }}>
          LiftForge v{APP_VERSION}
        </div>
        <div style={{ color: '#71717A', fontSize: '11px' }}>
          &copy; 2026 LiftForge. All rights reserved.
        </div>
      </div>

      {/* Additional Settings Links */}
      <div className="mt-6 space-y-3">
        <div
          className="w-full text-left p-4 rounded-xl"
          style={{ backgroundColor: '#27272A' }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span style={{ color: '#52525B', fontSize: '14px', fontWeight: 600 }}>
                Privacy Policy
              </span>
              <span style={{ color: '#52525B', fontSize: '12px', fontStyle: 'italic' }}>
                Coming soon
              </span>
            </div>
          </div>
        </div>

        <div
          className="w-full text-left p-4 rounded-xl"
          style={{ backgroundColor: '#27272A' }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span style={{ color: '#52525B', fontSize: '14px', fontWeight: 600 }}>
                Terms of Service
              </span>
              <span style={{ color: '#52525B', fontSize: '12px', fontStyle: 'italic' }}>
                Coming soon
              </span>
            </div>
          </div>
        </div>

        <div
          className="w-full text-left p-4 rounded-xl"
          style={{ backgroundColor: '#27272A' }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span style={{ color: '#52525B', fontSize: '14px', fontWeight: 600 }}>
                Help &amp; Support
              </span>
              <span style={{ color: '#52525B', fontSize: '12px', fontStyle: 'italic' }}>
                Coming soon
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
